/**
 * Single source of truth for wizard step slugs.
 *
 * The wizard URL uses human-readable slugs (`/signup/wizard/company`) instead
 * of numeric indices (`/signup/wizard/1`) for two reasons:
 *  - friendlier in the address bar and analytics
 *  - resilient to step reorders / inserts (a saved bookmark to `company`
 *    keeps working even if Company moves from step 1 to step 2)
 *
 * Order in this array IS the canonical step ordering. Adding a step =
 * appending here + adding the component in SignupWizard.vue's STEP_COMPONENTS.
 */
export const STEP_SLUGS = [
  'company',
  'values',
  'people',
  'rewards',
  'launch',
] as const

export type StepSlug = typeof STEP_SLUGS[number]

export const TOTAL_STEPS = STEP_SLUGS.length

export const FIRST_STEP_SLUG: StepSlug = STEP_SLUGS[0]

/**
 * Human-readable name for each step, surfaced in the topbar progress
 * eyebrow ("STEP 1 · YOUR WORKSPACE"). Keys must match STEP_SLUGS.
 *
 * Style: possessive-led ("Your X") for parallel scanning, with the finale
 * step using a forward-looking phrase ("Ready to launch") instead of a
 * bare noun. Bare nouns like "Company" or "Rewards" tell the user where
 * they are in the form, not what they're doing — possessive framing
 * makes the wizard feel like it's building *their* workspace, not asking
 * them to fill out a category.
 */
export const STEP_NAMES: Record<StepSlug, string> = {
  company: 'Your workspace',
  values:  'Your values',
  people:  'Your team',
  rewards: 'Your rewards',
  launch:  'Ready to launch',
}

export function isStepSlug(value: unknown): value is StepSlug {
  return typeof value === 'string' && (STEP_SLUGS as readonly string[]).includes(value)
}

/** 1-based index → slug. Clamps out-of-range to first step. */
export function slugFromIndex(index: number): StepSlug {
  const i = Math.max(1, Math.min(STEP_SLUGS.length, Math.round(index)))
  return STEP_SLUGS[i - 1]
}

/** slug → 1-based index. Returns 1 for unknown slugs. */
export function indexFromSlug(slug: string | undefined | null): number {
  if (!slug) return 1
  const i = (STEP_SLUGS as readonly string[]).indexOf(slug)
  return i === -1 ? 1 : i + 1
}

/** Build the canonical URL for a wizard step (by slug or 1-based index). */
export function wizardPathForStep(stepOrSlug: number | string): string {
  const slug = typeof stepOrSlug === 'number'
    ? slugFromIndex(stepOrSlug)
    : (isStepSlug(stepOrSlug) ? stepOrSlug : FIRST_STEP_SLUG)
  return `/signup/wizard/${slug}`
}
